import { useContext, useEffect, useState } from 'react';

import { postIncodeVerificationUrl } from 'PrivateApis';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { APIResponseStatus } from 'Services/api.types';

import { useVehicleCollectionSub } from 'Containers/app/vehicleCollection/hooks/useVehicleCollectionSub';

import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import { IncodeStepProps, SENTRY_TAG } from '../IncodeSDKHelpers';

export type IncodeSessionState = {
  session: IncodeStepProps['incodeSession'] | undefined;
  success: boolean | undefined;
};

export const useIncodeVerificationSession = () => {
  const { userState } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { offer, offerStatus } = useVehicleCollectionSub();

  const [incodeSession, setIncodeSession] = useState<IncodeSessionState>({ session: undefined, success: undefined });

  const { auth_token: authToken } = userState ?? {};
  const { dpEnquiryId } = vehicle?.enquiry ?? {};
  const isOnFinance = offer?.data?.finance?.isVehicleOnFinance;

  useEffect(() => {
    const abortController = new AbortController();

    if (offerStatus !== APIResponseStatus.SUCCESS) {
      return () => {};
    }

    if (!authToken || !dpEnquiryId) {
      setIncodeSession({ session: undefined, success: false });
      return () => {};
    }

    postIncodeVerificationUrl(dpEnquiryId, { Authorization: authToken }, { isOnFinance, mode: 'sdk' }, abortController)
      .then((res) => {
        if (!res?.session) {
          throw new Error('No incode session returned');
        }
        INCODE_ANALYTICS.INCODE_ADD_CONTEXT(res.session);
        setIncodeSession({ session: res.session, success: true });
      })
      .catch((err) => {
        globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
        setIncodeSession({ session: undefined, success: false });
      });

    return () => {
      abortController?.abort();
    };
  }, [authToken, dpEnquiryId, isOnFinance, offerStatus]);

  return { incodeSession };
};
